import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { api, type CellComment, type ReaderCellComment, type SessionComment, type MessengerMessage } from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useClickOutside } from '@/hooks/useClickOutside';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { generateUUID } from '@/utils/generateUUID';
import { Button } from '@/components/ui/Button/Button';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { CloseIcon, SendIcon } from '@/components/ui/Icons';
import { MessageStrip } from '@/components/Messenger/shared/composer/MessageStrip';
import { CellCommentsThreadItem } from './CellCommentsThreadItem';
import type { CellCommentInteractionMode, CellCommentThreadTarget } from './CellCommentsTableContext';
import {
  CELL_COMMENTS_PAGE_SIZE,
  cellCommentAuthorDisplayLabel,
  getCellCommentsInfiniteQueryOptions,
  getReaderCellCommentsInfiniteQueryOptions,
  getSessionCommentsInfiniteQueryOptions,
} from './cellCommentsQuery';
import './CellCommentsThreadPopover.css';

type ThreadComment = CellComment | ReaderCellComment | SessionComment;
type ThreadCommentsPage = {
  items: ThreadComment[];
  total: number;
  limit: number;
  offset: number;
};

const TEMP_COMMENT_PREFIX = 'temp-cc-';

function isOptimisticCellCommentId(id: string): boolean {
  return id.startsWith(TEMP_COMMENT_PREFIX);
}

function authorLabelForComment(c: ThreadComment, viewerId: string | null): string {
  return cellCommentAuthorDisplayLabel(c, viewerId);
}

function buildOptimisticCellComment(params: {
  tempId: string;
  projectId: string;
  columnId: string;
  body: string;
  authorPersonId: string;
  authorDisplayName: string;
  replyToId?: string | null;
  replyTo?: ThreadComment | null;
}): CellComment {
  const replyTo =
    params.replyToId && params.replyTo
      ? {
          id: params.replyTo.id,
          body: params.replyTo.body,
          author_name: authorLabelForComment(params.replyTo, params.authorPersonId),
        }
      : null;
  return {
    id: params.tempId,
    project_id: params.projectId,
    column_id: params.columnId,
    body: params.body,
    author_person_id: params.authorPersonId,
    author_name: params.authorDisplayName.trim() ? params.authorDisplayName.trim() : null,
    created_at: new Date().toISOString(),
    edited_at: null,
    reply_to_id: params.replyToId ?? null,
    reply_to: replyTo,
    can_delete: true,
    can_edit: true,
  };
}

function buildOptimisticSessionComment(params: {
  tempId: string;
  sessionId: string;
  body: string;
  authorPersonId: string;
  authorDisplayName: string;
  replyToId?: string | null;
  replyTo?: ThreadComment | null;
}): SessionComment {
  const replyTo =
    params.replyToId && params.replyTo
      ? {
          id: params.replyTo.id,
          body: params.replyTo.body,
          author_name: authorLabelForComment(params.replyTo, params.authorPersonId),
        }
      : null;
  return {
    id: params.tempId,
    session_id: params.sessionId,
    body: params.body,
    author_person_id: params.authorPersonId,
    author_name: params.authorDisplayName.trim() ? params.authorDisplayName.trim() : null,
    created_at: new Date().toISOString(),
    edited_at: null,
    reply_to_id: params.replyToId ?? null,
    reply_to: replyTo,
    can_delete: true,
    can_edit: true,
  };
}

function buildOptimisticReaderCellComment(params: {
  tempId: string;
  readerPersonId: string;
  columnId: string;
  body: string;
  authorPersonId: string;
  authorDisplayName: string;
}): ReaderCellComment {
  return {
    id: params.tempId,
    reader_person_id: params.readerPersonId,
    column_id: params.columnId,
    body: params.body,
    author_person_id: params.authorPersonId,
    author_name: params.authorDisplayName.trim() ? params.authorDisplayName.trim() : null,
    created_at: new Date().toISOString(),
    edited_at: null,
    reply_to_id: null,
    reply_to: null,
    can_delete: true,
    can_edit: true,
  };
}

function optimisticAddComment(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  comment: ThreadComment
): InfiniteData<ThreadCommentsPage> {
  if (!old || old.pages.length === 0) {
      return {
        pages: [{ items: [comment], total: 1, limit: CELL_COMMENTS_PAGE_SIZE, offset: 0 }],
      pageParams: ['init'],
    };
  }
  const baseTotal = (old.pages[0]?.total ?? 0) + 1;
  const pages = old.pages.map((p, i) =>
    i === 0
      ? { ...p, items: [...p.items, comment], total: baseTotal }
      : { ...p, total: baseTotal }
  );
  return { ...old, pages };
}

function optimisticReplaceComment(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  commentId: string,
  body: string
): InfiniteData<ThreadCommentsPage> | undefined {
  if (!old) return old;
  const pages = old.pages.map((p) => ({
    ...p,
    items: p.items.map((c) =>
      c.id === commentId ? { ...c, body, edited_at: new Date().toISOString() } : c
    ),
  }));
  return { ...old, pages };
}

function optimisticRemoveComment(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  commentId: string
): InfiniteData<ThreadCommentsPage> | undefined {
  if (!old) return old;
  const baseTotal = Math.max(0, (old.pages[0]?.total ?? 0) - 1);
  const pages = old.pages.map((p) => ({
    ...p,
    items: p.items.filter((c) => c.id !== commentId),
    total: baseTotal,
  }));
  return { ...old, pages };
}

function replaceTempCommentWithServer(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  tempId: string,
  server: ThreadComment
): InfiniteData<ThreadCommentsPage> | undefined {
  if (!old) return old;
  const pages = old.pages.map((p) => ({
    ...p,
    items: p.items.map((c) => (c.id === tempId ? server : c)),
  }));
  return { ...old, pages };
}

function replaceCommentWithServer(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  commentId: string,
  server: ThreadComment
): InfiniteData<ThreadCommentsPage> | undefined {
  if (!old) return old;
  const pages = old.pages.map((p) => ({
    ...p,
    items: p.items.map((c) => (c.id === commentId ? server : c)),
  }));
  return { ...old, pages };
}

function upsertCommentWithServer(
  old: InfiniteData<ThreadCommentsPage> | undefined,
  server: ThreadComment
): InfiniteData<ThreadCommentsPage> {
  const existing = old?.pages.some((page) => page.items.some((comment) => comment.id === server.id));
  return existing ? (replaceCommentWithServer(old, server.id, server) ?? optimisticAddComment(old, server)) : optimisticAddComment(old, server);
}

function formatCommentTime(iso: string): string {
  try {
    const d = parseISO(iso);
    if (isToday(d)) return format(d, "'сегодня в' HH:mm", { locale: ru });
    if (isYesterday(d)) return format(d, "'вчера в' HH:mm", { locale: ru });
    return format(d, "dd.MM.yyyy 'в' HH:mm", { locale: ru });
  } catch {
    return iso;
  }
}

interface ThreadListProps {
  comments: ThreadComment[];
  isLoading: boolean;
  error: string | null;
  currentUserId: string | null;
  onEdit: (c: ThreadComment) => void;
  onReply: (c: ThreadComment) => void;
  onDelete: (id: string) => void;
  deletePendingId: string | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onCommentContextMenuOpen: () => void;
  canReply: boolean;
}

function CellCommentsThreadList({
  comments,
  isLoading,
  error,
  currentUserId,
  onEdit,
  onReply,
  onDelete,
  deletePendingId,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onCommentContextMenuOpen,
  canReply,
}: ThreadListProps) {
  if (isLoading) {
    return (
      <div className="cell-comments-thread__loading" aria-busy="true" aria-live="polite">
        <span className="cell-comments-thread__loading-spinner" aria-label="Загрузка комментариев" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="cell-comments-thread__error" role="alert">
        {error}
      </div>
    );
  }
  if (comments.length === 0) {
    return (
      <div className="cell-comments-thread__empty">
        <i className="fa-regular fa-message cell-comments-thread__empty-icon" aria-hidden="true" />
        <p>Нет комментариев</p>
      </div>
    );
  }
  return (
    <div className="cell-comments-thread__feed">
      {hasNextPage ? (
        <div className="cell-comments-thread__load-older">
          <Button
            type="button"
            variant="outlined"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Загрузка…' : 'Показать предыдущие'}
          </Button>
        </div>
      ) : null}
      <ul className="cell-comments-thread__list" aria-live="polite">
        {comments.map((c) => {
          const deliveryPending = isOptimisticCellCommentId(c.id);
          const timeLabel = formatCommentTime(c.created_at);
          const edited = Boolean(c.edited_at);
          const authorName = cellCommentAuthorDisplayLabel(c, currentUserId);
          const showEditBtn = Boolean(c.can_edit);
          const showDeleteBtn = Boolean(c.can_delete);

          return (
            <CellCommentsThreadItem
              key={c.id}
              comment={c}
              authorName={authorName}
              timeLabel={timeLabel}
              deliveryPending={deliveryPending}
              edited={edited}
              showEdit={showEditBtn}
              showDelete={showDeleteBtn}
              deletePending={deletePendingId === c.id}
              onEdit={onEdit}
              onReply={onReply}
              onDelete={onDelete}
              onCommentContextMenuOpen={onCommentContextMenuOpen}
              canReply={canReply}
            />
          );
        })}
      </ul>
    </div>
  );
}

interface CellCommentsThreadPopoverProps {
  threadTarget: CellCommentThreadTarget;
  triggerRef: React.RefObject<HTMLElement | null>;
  interactionMode: CellCommentInteractionMode;
  isOpen: boolean;
  onClose: () => void;
  cancelHoverDismiss: () => void;
  scheduleHoverDismiss: () => void;
  /** Перевести тред в закреплённый режим (например при ПКМ по комментарию). */
  onRequestPin: () => void;
}

export function CellCommentsThreadPopover({
  threadTarget,
  triggerRef,
  interactionMode,
  isOpen,
  onClose,
  cancelHoverDismiss,
  scheduleHoverDismiss,
  onRequestPin,
}: CellCommentsThreadPopoverProps) {
  const { user } = useAuth();
  const { showError } = useToast();
  const currentUserId = user?.id ?? null;
  const currentUserDisplayName =
    user != null ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : '';
  const queryClient = useQueryClient();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const editTargetRef = useRef<{ id: string; body: string } | null>(null);
  /** Черновик нового комментария до входа в режим правки — восстанавливается после сохранения/отмены */
  const stashedNewCommentDraftRef = useRef<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<{ id: string; body: string } | null>(null);
  const [replyTarget, setReplyTarget] = useState<ThreadComment | null>(null);
  const replyTargetRef = useRef<ThreadComment | null>(null);
  editTargetRef.current = editTarget;
  replyTargetRef.current = replyTarget;

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(isOpen, onClose, true);

  const threadTitle =
    threadTarget.kind === 'project'
      ? threadTarget.projectName
      : threadTarget.kind === 'reader'
        ? threadTarget.readerName
        : threadTarget.title;
  const canReply = threadTarget.kind !== 'reader';

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    priorityAxis: 'x',
    preferredPositionX: 'right',
    preferredPositionY: 'bottom',
    horizontalAlign: 'center',
    verticalAlign: 'center',
    offset: 4,
    zIndex: 1001,
    dependencies: [threadTarget],
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  useClickOutside(popoverRef, () => requestClose(), {
    enabled: shouldRender,
    excludeRefs: [triggerRef as React.RefObject<HTMLElement | null>],
    /**
     * context-menu — портал; shell и бейдж — другие ячейки: иначе mousedown (см. useClickOutside)
     * раньше onClick закрывает тред при переключении строки (pointerdown на бейдже не гасит mousedown).
     */
    excludeSelector: '.context-menu, .project-table-cell-comment-shell, .cell-comment-corner-badge',
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender || interactionMode !== 'hover') return;
    const root = popoverRef.current;
    if (!root) return;

    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && root.contains(next)) return;
      window.setTimeout(() => {
        if (!root.contains(document.activeElement)) {
          scheduleHoverDismiss();
        }
      }, 0);
    };

    root.addEventListener('focusout', onFocusOut);
    return () => root.removeEventListener('focusout', onFocusOut);
  }, [shouldRender, interactionMode, scheduleHoverDismiss]);

  useEffect(() => {
    if (!shouldRender || interactionMode !== 'pinned') return;
    const root = popoverRef.current;
    if (!root) return;

    const focusComposer = () => {
      root.querySelector<HTMLTextAreaElement>('textarea')?.focus();
    };

    const id = window.requestAnimationFrame(() => focusComposer());

    const selector =
      'button:not([disabled]), [href], textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !root) return;
      const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => !el.hidden && el.tabIndex !== -1
      );
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    root.addEventListener('keydown', onKeydown);
    return () => {
      window.cancelAnimationFrame(id);
      root.removeEventListener('keydown', onKeydown);
    };
  }, [shouldRender, interactionMode, threadTarget]);

  const hoverSurfaceProps =
    interactionMode === 'hover'
      ? {
          onPointerEnter: () => cancelHoverDismiss(),
          onPointerLeave: (e: React.PointerEvent) => {
            const rel = e.relatedTarget;
            if (rel instanceof Element && rel.closest('.context-menu')) {
              cancelHoverDismiss();
              return;
            }
            window.requestAnimationFrame(() => {
              const el = document.elementFromPoint(e.clientX, e.clientY);
              if (el instanceof Element && el.closest('.context-menu')) {
                cancelHoverDismiss();
                return;
              }
              scheduleHoverDismiss();
            });
          },
        }
      : {};

  /** Любое взаимодействие с тредом в режиме hover переводит его в pinned — иначе focusout / dismiss закрывают попап при «Отправить», удалении и т.д. */
  const pinThreadIfHoverFromEventTarget = useCallback(
    (target: EventTarget | null) => {
      if (interactionMode !== 'hover') return;
      if (!(target instanceof Element)) return;
      if (target.closest('.cell-comments-thread__close')) return;
      onRequestPin();
    },
    [interactionMode, onRequestPin],
  );

  const handleThreadPointerDownCapture = useCallback(
    (e: React.PointerEvent) => {
      pinThreadIfHoverFromEventTarget(e.target);
    },
    [pinThreadIfHoverFromEventTarget],
  );

  const handleThreadFocusCapture = useCallback(
    (e: React.FocusEvent) => {
      pinThreadIfHoverFromEventTarget(e.target);
    },
    [pinThreadIfHoverFromEventTarget],
  );

  const projectThreadQuery = useInfiniteQuery({
    ...getCellCommentsInfiniteQueryOptions(
      threadTarget.kind === 'project' ? threadTarget.projectId : '',
      threadTarget.kind === 'project' ? threadTarget.columnId : ''
    ),
    enabled: shouldRender && threadTarget.kind === 'project',
  });
  const sessionThreadQuery = useInfiniteQuery({
    ...getSessionCommentsInfiniteQueryOptions(
      threadTarget.kind === 'session' ? threadTarget.sessionId : ''
    ),
    enabled: shouldRender && threadTarget.kind === 'session',
  });
  const readerThreadQuery = useInfiniteQuery({
    ...getReaderCellCommentsInfiniteQueryOptions(
      threadTarget.kind === 'reader' ? threadTarget.readerPersonId : '',
      threadTarget.kind === 'reader' ? threadTarget.columnId : ''
    ),
    enabled: shouldRender && threadTarget.kind === 'reader',
  });
  const commentsInfinite =
    threadTarget.kind === 'project'
      ? projectThreadQuery
      : threadTarget.kind === 'reader'
        ? readerThreadQuery
        : sessionThreadQuery;

  const comments = useMemo(
    () =>
      commentsInfinite.data
        ? [...commentsInfinite.data.pages].reverse().flatMap((p) => p.items as ThreadComment[])
        : [],
    [commentsInfinite.data]
  );

  const threadQueryKey = useMemo((): readonly [string, string] | readonly [string, string, string] => {
    if (threadTarget.kind === 'project') {
      return ['cellComments', threadTarget.projectId, threadTarget.columnId];
    }
    if (threadTarget.kind === 'reader') {
      return ['readerCellComments', threadTarget.readerPersonId, threadTarget.columnId];
    }
    return ['sessionComments', threadTarget.sessionId];
  }, [threadTarget]);

  const invalidateSummariesOnly = useCallback(() => {
    if (threadTarget.kind === 'project') {
      invalidateSummariesForProjectIds(queryClient, [threadTarget.projectId]);
    } else if (threadTarget.kind === 'reader') {
      void queryClient.invalidateQueries({ queryKey: ['readersTableSummaries'] });
    } else {
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesRange'] });
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesModal'] });
    }
  }, [queryClient, threadTarget]);

  type CreateMutateInput = {
    body: string;
    reply_to_id?: string;
    /** Только для оптимистичного UI (не уходит на сервер) */
    _replyToForOptimistic?: ThreadComment | null;
  };

  const createMut = useMutation<
    ThreadComment,
    Error,
    CreateMutateInput,
    { previous: InfiniteData<ThreadCommentsPage> | undefined; tempId?: string } | undefined
  >({
    mutationFn: (input: CreateMutateInput) => {
      if (threadTarget.kind === 'project') {
        return api.projects.createCellComment(threadTarget.projectId, {
          column_id: threadTarget.columnId,
          body: input.body,
          reply_to_id: input.reply_to_id,
        });
      }
      if (threadTarget.kind === 'reader') {
        return api.readers.createCellComment(threadTarget.readerPersonId, {
          column_id: threadTarget.columnId,
          body: input.body,
        });
      }
      return api.sessions.createSessionComment(threadTarget.sessionId, {
        body: input.body,
        reply_to_id: input.reply_to_id,
      });
    },
    onMutate: async (input) => {
      if (threadTarget.kind === 'project') {
        markProjectUpdatedByMutation(threadTarget.projectId);
      }
      await queryClient.cancelQueries({ queryKey: threadQueryKey });
      const previous = queryClient.getQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey);
      if (!currentUserId) return { previous };
      const tempId = `${TEMP_COMMENT_PREFIX}${generateUUID()}`;
      const optimistic: ThreadComment =
        threadTarget.kind === 'project'
          ? buildOptimisticCellComment({
              tempId,
              projectId: threadTarget.projectId,
              columnId: threadTarget.columnId,
              body: input.body,
              authorPersonId: currentUserId,
              authorDisplayName: currentUserDisplayName || 'Вы',
              replyToId: input.reply_to_id,
              replyTo: input._replyToForOptimistic ?? null,
            })
          : threadTarget.kind === 'reader'
            ? buildOptimisticReaderCellComment({
                tempId,
                readerPersonId: threadTarget.readerPersonId,
                columnId: threadTarget.columnId,
                body: input.body,
                authorPersonId: currentUserId,
                authorDisplayName: currentUserDisplayName || 'Вы',
              })
          : buildOptimisticSessionComment({
              tempId,
              sessionId: threadTarget.sessionId,
              body: input.body,
              authorPersonId: currentUserId,
              authorDisplayName: currentUserDisplayName || 'Вы',
              replyToId: input.reply_to_id,
              replyTo: input._replyToForOptimistic ?? null,
            });
      queryClient.setQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey, (old) =>
        optimisticAddComment(old, optimistic)
      );
      return { previous, tempId };
    },
    onError: (e: Error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(threadQueryKey, context.previous);
      }
      const msg = e instanceof Error ? e.message : String(e);
      showError(msg);
      setLocalError(e.message);
    },
    onSuccess: (data, _input, context) => {
      queryClient.setQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey, (old) =>
        context?.tempId
          ? replaceTempCommentWithServer(old, context.tempId, data)
          : upsertCommentWithServer(old, data)
      );
      stashedNewCommentDraftRef.current = null;
      setReplyTarget(null);
      setDraft('');
      setLocalError(null);
      invalidateSummariesOnly();
    },
  });

  const updateMut = useMutation<
    ThreadComment,
    Error,
    { commentId: string; body: string },
    { previous: InfiniteData<ThreadCommentsPage> | undefined } | undefined
  >({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) => {
      if (threadTarget.kind === 'project') {
        return api.projects.updateCellComment(threadTarget.projectId, commentId, { body });
      }
      if (threadTarget.kind === 'reader') {
        return api.readers.updateCellComment(threadTarget.readerPersonId, commentId, { body });
      }
      return api.sessions.updateSessionComment(threadTarget.sessionId, commentId, { body });
    },
    onMutate: async ({ commentId, body }) => {
      if (threadTarget.kind === 'project') {
        markProjectUpdatedByMutation(threadTarget.projectId);
      }
      await queryClient.cancelQueries({ queryKey: threadQueryKey });
      const previous = queryClient.getQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey);
      queryClient.setQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey, (old) =>
        optimisticReplaceComment(old, commentId, body)
      );
      return { previous };
    },
    onError: (e: Error, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(threadQueryKey, context.previous);
      }
      const msg = e instanceof Error ? e.message : String(e);
      showError(msg);
      setLocalError(e.message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey, (old) =>
        replaceCommentWithServer(old, data.id, data)
      );
      setEditTarget(null);
      const s = stashedNewCommentDraftRef.current;
      stashedNewCommentDraftRef.current = null;
      setDraft(s ?? '');
      setLocalError(null);
      invalidateSummariesOnly();
    },
  });

  const deleteMut = useMutation<void, Error, string, { previous: InfiniteData<ThreadCommentsPage> | undefined } | undefined>({
    mutationFn: (commentId: string) => {
      if (threadTarget.kind === 'project') {
        return api.projects.deleteCellComment(threadTarget.projectId, commentId);
      }
      if (threadTarget.kind === 'reader') {
        return api.readers.deleteCellComment(threadTarget.readerPersonId, commentId);
      }
      return api.sessions.deleteSessionComment(threadTarget.sessionId, commentId);
    },
    onMutate: async (commentId) => {
      if (threadTarget.kind === 'project') {
        markProjectUpdatedByMutation(threadTarget.projectId);
      }
      await queryClient.cancelQueries({ queryKey: threadQueryKey });
      const previous = queryClient.getQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey);
      queryClient.setQueryData<InfiniteData<ThreadCommentsPage>>(threadQueryKey, (old) =>
        optimisticRemoveComment(old, commentId)
      );
      return { previous };
    },
    onError: (e: Error, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(threadQueryKey, context.previous);
      }
      const msg = e instanceof Error ? e.message : String(e);
      showError(msg);
      setLocalError(e.message);
    },
    onSuccess: (_data, commentId) => {
      setLocalError(null);
      if (editTarget?.id === commentId) {
        setEditTarget(null);
        const s = stashedNewCommentDraftRef.current;
        stashedNewCommentDraftRef.current = null;
        setDraft(s ?? '');
      }
      if (replyTargetRef.current?.id === commentId) {
        setReplyTarget(null);
        const s = stashedNewCommentDraftRef.current;
        stashedNewCommentDraftRef.current = null;
        setDraft(s ?? '');
      }
      invalidateSummariesOnly();
    },
  });

  const requestDeleteComment = useCallback(
    (commentId: string) => {
      if (deleteMut.isPending) return;
      if (!window.confirm('Удалить комментарий?')) return;
      deleteMut.mutate(commentId);
    },
    [deleteMut]
  );

  const trimmedDraft = draft.trim();
  const editOriginalTrimmed = editTarget ? editTarget.body.trim() : '';
  const editSaveDisabled =
    !trimmedDraft || (editTarget != null && trimmedDraft === editOriginalTrimmed);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!trimmedDraft) {
        setLocalError('Введите текст');
        return;
      }
      setLocalError(null);
      if (editTarget) {
        if (editSaveDisabled) return;
        updateMut.mutate({ commentId: editTarget.id, body: trimmedDraft });
        return;
      }
      createMut.mutate({
        body: trimmedDraft,
        reply_to_id: canReply ? replyTarget?.id : undefined,
        _replyToForOptimistic: canReply ? replyTarget ?? undefined : undefined,
      });
    },
    [trimmedDraft, editTarget, editSaveDisabled, replyTarget, canReply, createMut, updateMut]
  );

  const cancelEdit = useCallback(() => {
    setEditTarget(null);
    const s = stashedNewCommentDraftRef.current;
    stashedNewCommentDraftRef.current = null;
    setDraft(s ?? '');
    setLocalError(null);
  }, []);

  const cancelReply = useCallback(() => {
    setReplyTarget(null);
    const s = stashedNewCommentDraftRef.current;
    stashedNewCommentDraftRef.current = null;
    setDraft(s ?? '');
    setLocalError(null);
  }, []);

  const startReply = useCallback((c: ThreadComment) => {
    if (!canReply) return;
    setLocalError(null);
    if (editTargetRef.current) {
      setEditTarget(null);
      const s = stashedNewCommentDraftRef.current;
      stashedNewCommentDraftRef.current = null;
      setDraft(s ?? '');
    }
    if (replyTargetRef.current?.id === c.id) return;
    if (replyTargetRef.current && replyTargetRef.current.id !== c.id) {
      setReplyTarget(c);
      return;
    }
    stashedNewCommentDraftRef.current = draftRef.current;
    setReplyTarget(c);
    setDraft('');
  }, [canReply]);

  const startEdit = useCallback((c: ThreadComment) => {
    setLocalError(null);
    if (replyTargetRef.current) {
      const s = stashedNewCommentDraftRef.current;
      stashedNewCommentDraftRef.current = null;
      setReplyTarget(null);
      setEditTarget({ id: c.id, body: c.body });
      setDraft(c.body);
      stashedNewCommentDraftRef.current = s ?? '';
      return;
    }
    if (editTargetRef.current === null) {
      stashedNewCommentDraftRef.current = draftRef.current;
    }
    setEditTarget({ id: c.id, body: c.body });
    setDraft(c.body);
  }, []);

  const editMessageStripPayload = useMemo(() => {
    if (!editTarget) return null;
    const message: MessengerMessage = {
      id: editTarget.id,
      thread_id: '',
      sender_id: '',
      body: editTarget.body,
      created_at: '',
    };
    const c = comments.find((x) => x.id === editTarget.id) as ThreadComment | undefined;
    const senderLabel = c == null ? 'Комментарий' : cellCommentAuthorDisplayLabel(c, currentUserId);
    return { message, senderLabel };
  }, [editTarget, comments, currentUserId]);

  const replyMessageStripPayload = useMemo(() => {
    if (!replyTarget) return null;
    const message: MessengerMessage = {
      id: replyTarget.id,
      thread_id: '',
      sender_id: replyTarget.author_person_id ?? '',
      body: replyTarget.body,
      created_at: replyTarget.created_at,
    };
    const senderLabel = cellCommentAuthorDisplayLabel(replyTarget, currentUserId);
    return { message, senderLabel };
  }, [replyTarget, currentUserId]);

  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editTarget) {
        e.preventDefault();
        cancelEdit();
        return;
      }
      if (replyTarget) {
        e.preventDefault();
        cancelReply();
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldRender, requestClose, editTarget, replyTarget, cancelEdit, cancelReply]);

  if (!shouldRender) return null;

  const fetchError =
    commentsInfinite.isError && commentsInfinite.error ? (commentsInfinite.error as Error).message : null;

  const submitPending = editTarget ? updateMut.isPending : createMut.isPending;
  const sendDisabled = submitPending || (editTarget ? editSaveDisabled : !trimmedDraft);

  return createPortal(
    <div
      ref={popoverRef}
      data-cell-comments-thread-root="true"
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: 'auto',
      }}
      {...hoverSurfaceProps}
    >
      <OverlayIntrinsicSize>
        <div
          className={`cell-comments-thread ${contentClasses}`}
          role="dialog"
          aria-modal={interactionMode === 'pinned'}
          aria-labelledby="cell-comments-thread-title"
          onPointerDownCapture={handleThreadPointerDownCapture}
          onFocusCapture={handleThreadFocusCapture}
        >
      <div className="cell-comments-thread__header">
        <h2 id="cell-comments-thread-title" className="cell-comments-thread__title">
          Комментарии: {threadTitle}
        </h2>
        <IconButton
          aria-label="Закрыть"
          onClick={requestClose}
          size="m"
          iconSize={20}
          variant="danger"
          className="cell-comments-thread__close"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className="cell-comments-thread__body">
        <OverlayScrollArea
          className="cell-comments-thread__scroll"
          osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
        >
          <div className="cell-comments-thread__scroll-pad">
            <CellCommentsThreadList
              comments={comments}
              isLoading={commentsInfinite.isLoading}
              error={fetchError}
              currentUserId={currentUserId}
              onEdit={startEdit}
              onReply={startReply}
              onDelete={requestDeleteComment}
              deletePendingId={deleteMut.isPending && deleteMut.variables ? deleteMut.variables : null}
              hasNextPage={commentsInfinite.hasNextPage}
              isFetchingNextPage={commentsInfinite.isFetchingNextPage}
              onLoadMore={() => void commentsInfinite.fetchNextPage()}
              onCommentContextMenuOpen={onRequestPin}
              canReply={canReply}
            />
          </div>
        </OverlayScrollArea>

        <div className="cell-comments-thread__composer-shell">
          {editMessageStripPayload ? (
            <MessageStrip
              variant="edit"
              message={editMessageStripPayload.message}
              senderLabel={editMessageStripPayload.senderLabel}
              onCancel={cancelEdit}
            />
          ) : replyMessageStripPayload ? (
            <MessageStrip
              variant="reply"
              message={replyMessageStripPayload.message}
              senderLabel={replyMessageStripPayload.senderLabel}
              onCancel={cancelReply}
            />
          ) : null}
          <form className="cell-comments-chatAssistant__inputArea" onSubmit={handleSubmit}>
            <div className="cell-comments-chatAssistant__inputRow">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={
                  editTarget
                    ? 'Измените комментарий…'
                    : replyTarget
                      ? 'Написать ответ…'
                      : 'Написать комментарий…'
                }
                minRows={1}
                maxRows={8}
                fullWidth
                className="cell-comments-chatAssistant__input form__field_inline"
                inputClassName="cell-comments-chatAssistant__inputField"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    const ok = editTarget ? !editSaveDisabled && Boolean(trimmedDraft) : Boolean(trimmedDraft);
                    if (ok) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }
                }}
              />
              <button
                type="submit"
                disabled={sendDisabled}
                className="cell-comments-chatAssistant__sendBtn"
                title={editTarget ? 'Сохранить' : 'Отправить'}
                aria-label={editTarget ? 'Сохранить комментарий' : 'Отправить комментарий'}
              >
                <SendIcon size={20} />
              </button>
            </div>
          </form>
          {localError ? (
            <div className="cell-comments-thread__composer-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {localError}
            </div>
          ) : null}
        </div>
      </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
